import React from 'react';
import { Tooltip as AntToolTip } from 'antd';
import {
  TooltipCardColor,
  defaultTooltipContentStyles,
  defaultTooltipStyles,
} from '@/components/utils/DefaultStyles';
import type { TooltipPlacement, TooltipTrigger } from '@/components/utils/TypeUtils';
import './Tooltip.scss';

export const getBodyTooltipContainer = (): HTMLElement => document.body;

export interface IToolTipProps {
  /** Tooltip content */
  title: string | React.ReactElement;

  /** Type of trigger to enable tooltip */
  trigger?: TooltipTrigger;

  /** Tooltip placement */
  placement?: TooltipPlacement;

  /** Whether to show an arrow pointing to the tooltip */
  arrow?: boolean;

  /** Specify an optional className to be applied to the container node */
  overlayClassName?: string;

  /** Style of the tooltip */
  overlayStyle?: React.CSSProperties;

  /** Style of the tooltip content */
  overlayInnerStyle?: React.CSSProperties;

  /** Color of the tooltip */
  color?: string;

  /** Portal target — use document.body inside overflow-hidden modals */
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;

  /** Stacking order when portaled above modals */
  zIndex?: number;

  /** Pass in the child to which the tooltip will be applied */
  children: React.ReactNode;
}

/**
 * Tooltip is a message box that displays extra information about an element. It can appear on hover or click.
 */
export const ToolTip = React.memo(function ToolTip(props: Readonly<IToolTipProps>): JSX.Element {
  const {
    title,
    trigger = 'hover',
    arrow = true,
    color = TooltipCardColor,
    overlayClassName,
    placement = 'top',
    overlayStyle,
    children,
    overlayInnerStyle,
    getPopupContainer,
    zIndex,
  } = props;

  const mergedOverlayStyle = React.useMemo(
    () => (overlayStyle ? { ...defaultTooltipStyles, ...overlayStyle } : defaultTooltipStyles),
    [overlayStyle]
  );
  const mergedOverlayInnerStyle = React.useMemo(
    () =>
      overlayInnerStyle
        ? { ...defaultTooltipContentStyles, ...overlayInnerStyle }
        : defaultTooltipContentStyles,
    [overlayInnerStyle]
  );

  return (
    <AntToolTip
      title={title}
      trigger={trigger}
      placement={placement}
      arrow={arrow}
      overlayClassName={overlayClassName}
      overlayStyle={mergedOverlayStyle}
      overlayInnerStyle={mergedOverlayInnerStyle}
      color={color}
      getPopupContainer={getPopupContainer}
      zIndex={zIndex}
    >
      <span className="tooltip-trigger-wrap">{children}</span>
    </AntToolTip>
  );
});

ToolTip.displayName = 'ToolTip';
